import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Cyboard Logo — centralized brand asset.
 *
 * Props:
 *   variant:    'full' | 'icon' | 'wordmark' (default 'full')
 *   size:       'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero' (default 'md')
 *   clickable:  boolean — wraps in a Link to `/` (default true)
 *   showTagline: boolean — render "Play. Connect. Level Up." underneath
 *   className:  additional classes
 *
 * All logo usage across the app MUST go through this component.
 * Raw <img src="cyboard-logo.png" /> is forbidden.
 */

// Version query — increment to bust browser caches when assets change.
const ASSET_VERSION = 'v3';
export const LOGO_ASSETS = {
  full: `/cyboard-logo.png?${ASSET_VERSION}`,
  icon: `/cyboard-icon.png?${ASSET_VERSION}`,
  favicon: `/favicon.png?${ASSET_VERSION}`,
};

const HEIGHT_BY_SIZE = {
  xs: 18,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 72,
  hero: 120,
};

const ICON_SIZE_BY_SIZE = {
  xs: 20,
  sm: 28,
  md: 36,
  lg: 52,
  xl: 80,
  hero: 140,
};

export function Logo({
  variant = 'full',
  size = 'md',
  clickable = true,
  showTagline = false,
  className = '',
}) {
  const iconPx = ICON_SIZE_BY_SIZE[size] || ICON_SIZE_BY_SIZE.md;
  const heightPx = HEIGHT_BY_SIZE[size] || HEIGHT_BY_SIZE.md;

  const content = (
    <span
      className={`inline-flex flex-col items-center ${className}`}
      data-testid={`cyboard-logo-${variant}`}
      aria-label="Cyboard logo"
    >
      {variant === 'full' ? (
        <img
          src={LOGO_ASSETS.full}
          alt="Cyboard — Play. Connect. Level Up."
          style={{
            // The xl/hero variants embed the tagline inside the PNG, which is
            // wide-aspect. Clamp by viewport width on small screens so the
            // logo never bleeds off iPhone-width devices.
            height: 'auto',
            width: '100%',
            maxWidth: size === 'hero' ? 'min(420px, 80vw)'
                    : size === 'xl' ? 'min(320px, 72vw)'
                    : size === 'lg' ? 'min(240px, 60vw)'
                    : `${heightPx * 4}px`,
          }}
          className="select-none"
          loading="eager"
          draggable={false}
        />
      ) : variant === 'icon' ? (
        <img
          src={LOGO_ASSETS.icon}
          alt="Cyboard logo"
          width={iconPx}
          height={iconPx}
          className="select-none"
          loading="eager"
          draggable={false}
        />
      ) : (
        // wordmark — icon + CYBOARD text side by side, compact for navbars
        <span className="inline-flex items-center gap-2">
          <img
            src={LOGO_ASSETS.icon}
            alt=""
            width={iconPx}
            height={iconPx}
            className="select-none flex-shrink-0"
            loading="eager"
            draggable={false}
          />
          <span
            className="font-bold tracking-[0.18em] text-white"
            style={{
              fontFamily: 'Unbounded, system-ui, sans-serif',
              fontSize: heightPx * 0.52,
              lineHeight: 1,
            }}
          >
            CYBOARD
          </span>
        </span>
      )}

      {showTagline && (
        <span
          className="mt-2 text-[10px] sm:text-[11px] tracking-[0.24em] font-medium uppercase"
          style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #3B82F6 50%, #9D4CDD 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
          data-testid="cyboard-logo-tagline"
        >
          Play · Connect · Level Up
        </span>
      )}
    </span>
  );

  if (clickable) {
    return (
      <Link to="/" className="inline-flex items-center group hover:opacity-90 transition-opacity" aria-label="Go to home">
        {content}
      </Link>
    );
  }
  return content;
}

/**
 * LogoMark — convenience for the icon-only variant (eg favicon-like use in badges).
 */
export function LogoMark({ size = 'md', className = '' }) {
  return <Logo variant="icon" size={size} clickable={false} className={className} />;
}

/**
 * BrandFooter — small "© Cyboard" + icon + support email strip.
 * Use this in layouts instead of the plain SupportFooter when you want branding reinforcement.
 */
export function BrandFooter({ className = '' }) {
  return (
    <footer
      className={`mt-8 pt-5 pb-6 border-t border-white/[0.04] text-center ${className}`}
      data-testid="brand-footer"
    >
      <div className="inline-flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
        <img
          src={LOGO_ASSETS.icon}
          alt="Cyboard"
          width={18}
          height={18}
          className="select-none"
          draggable={false}
        />
        <span className="text-[11px] text-[#666]">© Cyboard · Play. Connect. Level Up.</span>
      </div>
    </footer>
  );
}

export default Logo;
